import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import {
  CountrySelector,
  FTextInput,
  ItemSelector,
} from '@pflegenavi/web-components';
import { type FormikProps, getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import type { IMangopayConnectFormValues } from './MangopayConnectForm';
import { LegalPersonType } from '@pflegenavi/service-provider-frontend/api';

interface MangopayConnectFormStep1Props {
  formik: FormikProps<IMangopayConnectFormValues>;
  countryOptions: Array<{
    label: string;
    value: string;
  }>;
  disabled: boolean;
}

export const MangopayConnectFormStep1: FC<MangopayConnectFormStep1Props> = ({
  formik,
  countryOptions,
  disabled,
}) => {
  const { t } = useTranslation();

  const onBlur = useCallback(() => {
    formik.setFieldTouched(
      'service_provider.headquarters_address.country_id',
      true
    );
  }, [formik]);

  const onChangeCountry = useCallback(
    (newCountryId: string) => {
      if (
        formik.values.service_provider?.headquarters_address.country_id ===
        newCountryId
      ) {
        return;
      }

      formik.setFieldValue(
        'service_provider.headquarters_address.country_id',
        newCountryId
      );
      formik.setFieldError(
        'service_provider.headquarters_address.country_id',
        undefined
      );
    },
    [formik]
  );

  const legalPersonTypeOptions = useMemo(() => {
    return [
      {
        label: t(
          'service-provider-app.connect-account.form.legal-person-type-options.business'
        ),
        value: LegalPersonType.Business,
      },
      {
        label: t(
          'service-provider-app.connect-account.form.legal-person-type-options.organization'
        ),
        value: LegalPersonType.Organization,
      },
      {
        label: t(
          'service-provider-app.connect-account.form.legal-person-type-options.soleTrader'
        ),
        value: LegalPersonType.SoleTrader,
      },
    ];
  }, [t]);

  const onBlurLegalPersonType = useCallback(() => {
    formik.setFieldTouched('service_provider.legal_person_type', true);
  }, [formik]);

  const onChangeLegalPersonType = useCallback(
    (newLegalPersonType: string) => {
      if (
        formik.values.service_provider?.legal_person_type === newLegalPersonType
      ) {
        return;
      }

      formik.setFieldValue(
        'service_provider.legal_person_type',
        newLegalPersonType
      );
      formik.setFieldError('service_provider.legal_person_type', undefined);
    },
    [formik]
  );

  return (
    <Stack gap={2}>
      <Typography color="grey.500">
        {t(
          'service-provider-app.connect-account.fill-company-details-mangopay'
        )}
      </Typography>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FTextInput
            id="service_provider.name"
            name="service_provider.name"
            label={t('service-provider-app.connect-account.form.company-name')}
            formik={formik}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FTextInput
            id="service_provider.company_number"
            name="service_provider.company_number"
            label={t(
              'service-provider-app.connect-account.form.company-number'
            )}
            formik={formik}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ItemSelector
            label={t(
              'service-provider-app.connect-account.form.legal-person-type'
            )}
            valueSelected={
              formik.values.service_provider?.legal_person_type || ''
            }
            options={legalPersonTypeOptions}
            onBlur={onBlurLegalPersonType}
            onChange={onChangeLegalPersonType}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FTextInput
            id="service_provider.email"
            name="service_provider.email"
            label={t('service-provider-app.connect-account.form.email')}
            formik={formik}
            fullWidth
            disabled={true}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FTextInput
            id="service_provider.phone"
            type="tel"
            name="service_provider.phone"
            label={t('service-provider-app.connect-account.form.phone')}
            formik={formik}
            fullWidth
            disabled={disabled}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FTextInput
            id="service_provider.headquarters_address.address"
            name="service_provider.headquarters_address.address"
            label={t('service-provider-app.connect-account.form.address')}
            formik={formik}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FTextInput
            id="service_provider.headquarters_address.zip_code"
            name="service_provider.headquarters_address.zip_code"
            label={t('service-provider-app.connect-account.form.zip-code')}
            formik={formik}
            disabled={disabled}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FTextInput
            id="service_provider.headquarters_address.city"
            name="service_provider.headquarters_address.city"
            label={t('service-provider-app.connect-account.form.city')}
            formik={formik}
            disabled={disabled}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <CountrySelector
            label={t('service-provider-app.connect-account.form.country')}
            onChangeCountry={onChangeCountry}
            onBlur={onBlur}
            countries={countryOptions}
            countryId={
              formik.values.service_provider?.headquarters_address.country_id ||
              ''
            }
            disabled={disabled}
            name={'service_provider.headquarters_address.country_id'}
            errorMessage={
              getIn(
                formik.touched,
                'service_provider.headquarters_address.country_id'
              ) &&
              getIn(
                formik.errors,
                'service_provider.headquarters_address.country_id'
              )
            }
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
